<script setup lang="ts">
import type { DownloadFormat } from '~/composables/download/useDownloadApi'
import type { CheckedDownloadItem, DownloadTreeNode, InclusionOption, InclusionOptions } from '~/types/download'
import { DOWNLOAD_MAX_ITEMS } from '#shared/constants'
import { isValidPageNumberSelection, parsePageNumberSelection } from '#shared/download/pageNumberSelection'
import { faChevronDown, faXmark } from '@fortawesome/pro-light-svg-icons'
import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { ComboboxAnchor, ComboboxContent, ComboboxItem, ComboboxItemIndicator, ComboboxRoot, ComboboxTrigger, ComboboxViewport, Label, RadioGroupIndicator, RadioGroupItem, RadioGroupRoot } from 'reka-ui'
// Keep this import. Otherwise, `<Modal>` resolves to this file and recurses
import Modal from '~/components/Modal.vue'
import Checkbox from '~/components/form/Checkbox.vue'
import InputError from '~/components/form/InputError.vue'
import { useBookDownloadBoundries } from '~/composables/download/useBookDownloadBoundaries'
import { useDownloadApi } from '~/composables/download/useDownloadApi'
import { useDownloadItems } from '~/composables/download/useDownloadItems'
import { useDownloadTocScroll } from '~/composables/download/useDownloadTocScroll'
import { generatePdfToc } from '~/utils/download/generatePdfToc'
import { isAcademicArticlePayload } from '~/utils/download/isAcademicArticlePayload'
import { isBookPayload } from '~/utils/download/isBookPayload'
import { createCheckedItemFromNode, findFirstSelectableBookNode, findTocNodeBySelectionKey, normalizeDownloadId } from '~/utils/download/treeSelection'

const props = withDefaults(defineProps<{
  toc: DownloadTreeNode[]
  selectedId?: string
  inclusionOptions?: InclusionOption[]
  showInclusionOptions?: boolean
  pageNumberSelection?: boolean
  renderLeafNodes?: boolean
  disableLovtekst?: boolean
  noAvailabilityLabel?: string
}>(), {
  inclusionOptions: () => [],
  showInclusionOptions: true,
  pageNumberSelection: false,
  renderLeafNodes: false,
  disableLovtekst: false,
  noAvailabilityLabel: 'Ingen kommentar.',
})
const emit = defineEmits<{ (e: 'confirm'): void, (e: 'cancel'): void, (e: 'close'): void }>()

const LAW_AND_COMMENTARY_OUTPUT_OPTIONS: Array<{ id: DownloadFormat, name: string }> = [
  { id: 'pdf', name: 'PDF' },
  { id: 'docx', name: 'Word' },
]

const PDF_OUTPUT_OPTIONS: Array<{ id: DownloadFormat, name: string }> = [
  { id: 'pdf', name: 'PDF' },
]

const { downloadFile } = useDownloadApi()
const { checkedItems, onToggleCheckedBulk } = useDownloadItems()
const { scrollToSelectionKey } = useDownloadTocScroll()

const modalRef = useTemplateRef<any>('innerModal')
const selectionMode = ref<'documentParts' | 'pagenumbers'>('documentParts')

const format = ref<DownloadFormat>('pdf')

const include = reactive<InclusionOptions>({
  lovtekst: !props.disableLovtekst,
  curatedReferences: true,
  frontPage: true,
  tableOfContents: true,
  colophonPage: false,
  markings: false,
  specificPageNumbers: '',
})

const shouldShowInclusionOptions = computed(() => props.showInclusionOptions && props.inclusionOptions.length > 0)
const isPageNumberMode = computed(() => props.pageNumberSelection && selectionMode.value === 'pagenumbers')

const requestState = ref<'idle' | 'loading' | 'success' | 'error'>('idle')
const errorMessage = ref<string>('')

const isSpecificPageNumbersValid = computed(() => isValidPageNumberSelection(include.specificPageNumbers ?? ''))
const selectedPageCount = computed(() => {
  if (!isPageNumberMode.value) {
    return 0
  }

  const value = include.specificPageNumbers?.trim() ?? ''
  if (!value) {
    return 0
  }

  const { pageNumbers } = parsePageNumberSelection(value)
  return pageNumbers.length
})

const isSpecificPageCountWithinLimit = computed(() => selectedPageCount.value <= DOWNLOAD_MAX_ITEMS)

const specificPageNumbersErrorMessage = computed(() => {
  if (!isPageNumberMode.value) {
    return ''
  }

  const value = include.specificPageNumbers?.trim() ?? ''
  if (!value) {
    return ''
  }

  return isSpecificPageNumbersValid.value
    ? (isSpecificPageCountWithinLimit.value ? '' : `Du kan maksimalt velge ${DOWNLOAD_MAX_ITEMS} sider.`)
    : 'Ugyldig format. Bruk tall, intervall eller kombinasjon adskilt med komma (f.eks. 90-91, 100).'
})

const isDownloadDisabled = computed(() => {
  if (requestState.value === 'loading') {
    return true
  }

  if (isPageNumberMode.value) {
    return !isSpecificPageNumbersValid.value || !isSpecificPageCountWithinLimit.value
  }

  return checkedItems.value.size < 1
})

const isBookDownload = computed(() => isBookPayload(props.toc[0]?.payload))

const isAcademicArticleDownload = computed(() => props.toc.some(node => isAcademicArticlePayload(node.payload)))

const outputOptions = computed(() => (isAcademicArticleDownload.value ? PDF_OUTPUT_OPTIONS : LAW_AND_COMMENTARY_OUTPUT_OPTIONS))
const selectedFormatLabel = computed(() => outputOptions.value.find(option => option.id === format.value)?.name ?? format.value.toLocaleUpperCase())

const selectableNodes = computed(() => props.toc.filter(node => node.selectable ?? Boolean(node.ezMainNodeId)))

const allDocumentPartsSelected = computed(() => {
  if (!isAcademicArticleDownload.value) {
    return false
  }

  if (selectableNodes.value.length < 1) {
    return false
  }

  return selectableNodes.value.every((node) => {
    const key = normalizeDownloadId(node.downloadId ?? node.id)
    return key ? checkedItems.value.has(key) : false
  })
})

function onToggleSelectAllDocumentParts(checked: boolean) {
  onToggleCheckedBulk({
    items: selectableNodes.value,
    checked,
  })
}

defineExpose({
  show: (item: CheckedDownloadItem) => {
    selectionMode.value = 'documentParts'

    if (!outputOptions.value.some(option => option.id === format.value)) {
      format.value = outputOptions.value[0]?.id ?? 'pdf'
    }

    checkedItems.value.clear()
    const key = normalizeDownloadId(item.downloadId ?? item.id)
    if (!isBookDownload.value && key) {
      scrollToSelectionKey(props.toc, key)
    }

    if (isBookDownload.value) {
      const selectedNode = key ? findTocNodeBySelectionKey(props.toc, key) : null
      const selectedPayload = selectedNode && isBookPayload(selectedNode.payload)
        ? selectedNode.payload
        : null
      const effectiveNode = selectedPayload?.pageNumber
        ? selectedNode
        : findFirstSelectableBookNode(props.toc)
      const selectedKey = normalizeDownloadId(effectiveNode?.downloadId ?? effectiveNode?.id)

      scrollToSelectionKey(props.toc, selectedKey ?? key)
    }

    modalRef.value?.show?.()
  },
  hide: () => modalRef.value?.hide?.(),
})

async function onConfirm() {
  const specificPageNumbers = include.specificPageNumbers?.trim() ?? ''

  const pdfToc = generatePdfToc(props.toc, checkedItems.value)
  const sortedItems = sortedValues(checkedItems.value, (a, b) => (a.tocIndex ?? 0) - (b.tocIndex ?? 0))
  let items = useBookDownloadBoundries(props.toc, sortedItems)

  if (isBookDownload.value && isPageNumberMode.value && items.length < 1) {
    const fallbackNode = findFirstSelectableBookNode(props.toc)
    const fallbackItem = fallbackNode ? createCheckedItemFromNode(fallbackNode) : null
    items = fallbackItem ? [fallbackItem] : []
  }

  if (checkedItems.value.size > DOWNLOAD_MAX_ITEMS) {
    requestState.value = 'error'
    errorMessage.value = `Du har valgt ${checkedItems.value.size} dokumentdeler. Vennligst velg færre (maks ${DOWNLOAD_MAX_ITEMS}).`
    return
  }

  if (isBookDownload.value && isPageNumberMode.value && selectedPageCount.value > DOWNLOAD_MAX_ITEMS) {
    requestState.value = 'error'
    errorMessage.value = `Du har valgt ${selectedPageCount.value} sider. Vennligst velg færre (maks ${DOWNLOAD_MAX_ITEMS}).`
    return
  }

  requestState.value = 'loading'

  try {
    const options: InclusionOptions = {
      ...include,
      specificPageNumbers: isPageNumberMode.value ? specificPageNumbers : undefined,
    }

    await downloadFile({
      items,
      format: format.value, // server lowercases it
      options,
      toc: pdfToc,
    })
    requestState.value = 'success'
    setTimeout(() => {
      requestState.value = 'idle'
      emit('confirm')
      modalRef.value?.hide?.()
    }, 1500)
  }
  catch (error) {
    console.error('Download failed', error)
    requestState.value = 'error'
    errorMessage.value = 'Kunne ikke laste ned filen. Prøv å markere færre dokumentdeler, eller prøv igjen senere.'
  }
}

function onCancel() {
  emit('cancel')
  modalRef.value?.hide?.()
}
</script>

<template>
  <Modal ref="innerModal" class="download-modal scrollbar-carbon" @close="() => emit('close')">
    <template #header>
      <h4 class="title-s reading-dark:text-white">
        Hva vil du laste ned?
      </h4>
      <ControlButton
        size="small"
        :toggle-selected="false"
        :icon="faXmark"
        class="bg-transparent hover:bg-transparent border rounded-lg"
        aria-label="Lukk nedlastingsvindu"
        @click="onCancel"
      />
    </template>
    <template #body>
      <div v-if="pageNumberSelection" class="flex">
        <RadioGroupRoot v-model="selectionMode" class="flex gap-m">
          <div class="flex items-center gap-xs">
            <RadioGroupItem
              id="rg-documentParts"
              value="documentParts"
              :class="radioGroupVariants({ item: 'base' })"
              aria-label="Dokumentdeler"
            >
              <RadioGroupIndicator :class="radioGroupVariants({ indicator: 'base' })" />
            </RadioGroupItem>
            <label for="rg-documentParts" class="text-small reading-dark:text-white">
              Dokumentdeler
            </label>
          </div>
          <div class="flex items-center gap-xs">
            <RadioGroupItem
              id="rg-pagenumbers"
              value="pagenumbers"
              :class="radioGroupVariants({ item: 'base' })"
              aria-label="Sidetall"
            >
              <RadioGroupIndicator :class="radioGroupVariants({ indicator: 'base' })" />
            </RadioGroupItem>
            <label for="rg-pagenumbers" class="text-small reading-dark:text-white">
              Sidetall
            </label>
          </div>
        </RadioGroupRoot>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-12 gap-l">
        <div class="md:col-span-3 flex flex-col gap-m min-w-0">
          <div class="flex flex-col gap-l">
            <div v-if="!isAcademicArticleDownload" class="flex flex-col gap-s">
              <Label for="format-combobox" class="text-small font-bold reading-dark:text-white">Format</Label>
              <ComboboxRoot v-slot="{ open }" v-model="format" class="relative">
                <ComboboxAnchor class="relative w-full max-w-[214px]" :class="comboboxVariants.anchor.base({ intent: 'outlined' })">
                  <ComboboxTrigger
                    id="format-combobox"
                    :class="comboboxVariants.anchor.trigger({ intent: 'outlined' })"
                    class="flex items-center justify-between w-full reading-dark:text-white"
                  >
                    <span>{{ selectedFormatLabel }}</span>
                    <Icon :icon="faChevronDown" size="sm" :class="{ 'rotate-180': open }" class="transition-all duration-500" />
                  </ComboboxTrigger>
                </ComboboxAnchor>
                <ComboboxContent
                  :class="comboboxVariants.content.base({ context: 'download' })" position="popper" side="bottom" align="start"
                >
                  <ComboboxViewport>
                    <ComboboxItem
                      v-for="opt in outputOptions"
                      :key="opt.id"
                      :value="opt.id"
                      class="hover:bg-surface-hover"
                      :class="comboboxVariants.content.item({ context: 'download' })"
                    >
                      <span>{{ opt.name }}</span>
                      <ComboboxItemIndicator class="ml-auto reading-dark:text-blurple-300">
                        ✓
                      </ComboboxItemIndicator>
                    </ComboboxItem>
                  </ComboboxViewport>
                </ComboboxContent>
              </ComboboxRoot>
            </div>
            <div v-if="shouldShowInclusionOptions" class="flex flex-col gap-[1.25rem]">
              <span class="text-small font-bold reading-dark:text-white">Inkluder</span>
              <div class="flex flex-col gap-s">
                <FormCheckbox
                  v-for="opt in inclusionOptions"
                  :key="opt.key"
                  v-model="include[opt.key]"
                  :disabled="opt.key === 'lovtekst' && disableLovtekst"
                  class="shrink-0"
                  :label="opt.label"
                />
              </div>
            </div>
          </div>
        </div>
        <div v-if="selectionMode === 'documentParts'" class="md:col-span-9 flex flex-col gap-s min-w-0">
          <div class="flex flex-col items-start gap-xs">
            <h5 id="document-parts-heading" class="text-small font-bold reading-dark:text-white">
              Dokumentdeler
            </h5>
            <Checkbox
              v-if="isAcademicArticleDownload"
              :model-value="allDocumentPartsSelected"
              class="shrink-0"
              label="Velg alt"
              @update:model-value="(checked: boolean) => onToggleSelectAllDocumentParts(checked)"
            />
          </div>
          <ul
            aria-labelledby="document-parts-heading"
            class="lg:scrollbar-carbon lg:overflow-y-auto pr-s pl-2xs flex flex-col gap-3xs pb-s"
            :class="pageNumberSelection ? 'h-[56vh]' : 'h-[60vh]'"
          >
            <DownloadItem
              v-for="child in toc"
              :key="child.id"
              :toc-item="child"
              :selected-id="selectedId"
              :render-leaf-nodes="renderLeafNodes"
              :no-availability-label="props.noAvailabilityLabel"
              :is-book-download="isBookDownload"
            />
          </ul>
        </div>
        <div v-else-if="selectionMode === 'pagenumbers'" class="md:col-span-9 flex flex-col gap-s min-w-0">
          <div class="flex flex-col gap-s h-[60vh]">
            <label for="specificPageNumbersInput" class="text-small reading-dark:text-white">Skriv inn sidetall og/eller sideutvalg adskilt med komma (f.eks. 90-91, 100)</label>
            <input
              id="specificPageNumbersInput"
              v-model="include.specificPageNumbers"
              type="text"
              placeholder="F.eks. 90-91, 100"
              inputmode="numeric"
              :aria-invalid="Boolean(specificPageNumbersErrorMessage)"
              class="max-w-[336px] rounded-lg p-2xs bg-transparent reading-dark:text-white reading-dark:placeholder:text-carbon-300 focus:outline-none focus:ring-2 focus:ring-blurple-300"
              :class="specificPageNumbersErrorMessage ? 'border-2 border-red-500 reading-dark:border-red-50' : 'border border-carbon-300 reading-dark:border-carbon-300'"
            >
            <InputError v-if="specificPageNumbersErrorMessage">
              {{ specificPageNumbersErrorMessage }}
            </InputError>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-between border-t border-carbon-200 reading-dark:border-carbon-400 pt-m">
        <div class="status min-h-[1.5rem]" aria-live="polite">
          <div v-if="requestState === 'loading'" class="flex items-center gap-2 text-carbon-400 reading-dark:text-carbon-200">
            <span class="inline-block h-4 w-4 border-2 border-blurple-500 reading-dark:border-blurple-300 border-t-transparent rounded-full animate-spin" />
            <span class="text-small">Forbereder nedlasting…</span>
          </div>
          <div v-else-if="requestState === 'success'" class="flex items-center gap-2 text-green-500 reading-dark:text-green-50">
            <Icon :icon="faCheck" />
            <span class="text-small">Nedlasting vellykket!</span>
          </div>
          <div v-else-if="requestState === 'error'" class="text-red-500 reading-dark:text-red-50 text-small">
            {{ errorMessage }}
          </div>
          <div v-else class="text-small text-transparent select-none">
            .
          </div>
        </div>
        <Button id="download-btn" aria-label="Last ned valgte utdrag" size="small" variant="primary" :disabled="isDownloadDisabled" @click="onConfirm">
          Last ned
        </Button>
      </div>
    </template>
  </Modal>
</template>

<style lang="postcss">
.ks-dialog.download-modal {
  @apply !rounded-4xl p-m lg:!p-l !max-w-[1100px] overflow-y-auto lg:overflow-hidden shadow-lg bg-surface-elevated;
}

.ks-dialog.download-modal .ks-dialog-header {
  @apply !w-full;
}

.ks-dialog.download-modal .ks-dialog-body {
  @apply max-h-[64vh] xs:max-lg:!overflow-y-auto !p-0;
}

.ks-dialog.download-modal .ks-dialog-footer {
  @apply pt-0;
}
</style>
